import React, { useState } from 'react';

// Mock resources data
const mockResources = [
  {
    id: 'res-001',
    title: 'Fractions Visual Guide',
    type: 'PDF',
    category: 'Teaching Materials',
    gradeLevel: '5',
    subject: 'Math',
    thumbnail: 'https://via.placeholder.com/150/3B82F6/FFFFFF?text=PDF',
    description: 'Visual guide to help students understand fraction concepts using diagrams and real-world examples.',
    downloadUrl: '#'
  },
  {
    id: 'res-002',
    title: 'Decimal Place Value Worksheets',
    type: 'PDF',
    category: 'Worksheets',
    gradeLevel: '5',
    subject: 'Math',
    thumbnail: 'https://via.placeholder.com/150/8B5CF6/FFFFFF?text=PDF',
    description: 'Printable worksheets for practicing decimal place value concepts.',
    downloadUrl: '#'
  },
  {
    id: 'res-003',
    title: 'Math Vocabulary Cards',
    type: 'PDF',
    category: 'Teaching Materials',
    gradeLevel: 'K-5',
    subject: 'Math',
    thumbnail: 'https://via.placeholder.com/150/EC4899/FFFFFF?text=PDF',
    description: 'Printable vocabulary cards for key math terms with visual aids.',
    downloadUrl: '#'
  },
  {
    id: 'res-004',
    title: 'Number Line Activities',
    type: 'Video',
    category: 'Lesson Plans',
    gradeLevel: '3-5',
    subject: 'Math',
    thumbnail: 'https://via.placeholder.com/150/10B981/FFFFFF?text=Video',
    description: 'Video tutorial showing engaging number line activities for the classroom.',
    downloadUrl: '#'
  },
  {
    id: 'res-005',
    title: 'Fraction Games Lesson Plan',
    type: 'Document',
    category: 'Lesson Plans',
    gradeLevel: '5',
    subject: 'Math',
    thumbnail: 'https://via.placeholder.com/150/F59E0B/FFFFFF?text=Doc',
    description: 'Complete lesson plan with hands-on fraction games for engaging classroom activities.',
    downloadUrl: '#'
  },
  {
    id: 'res-006',
    title: "Parent's Guide to Supporting Math Learning",
    type: 'PDF',
    category: 'Parent Resources',
    gradeLevel: 'K-8',
    subject: 'Math',
    thumbnail: 'https://via.placeholder.com/150/EF4444/FFFFFF?text=PDF',
    description: "Guide for parents on how to support their child's math learning at home.",
    downloadUrl: '#'
  },
];

const ResourcesScreen: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [activeGrade, setActiveGrade] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const categories = ['All', 'Teaching Materials', 'Lesson Plans', 'Worksheets', 'Parent Resources'];
  const gradeOptions = ['All', 'K', '1', '2', '3', '4', '5', '6', '7', '8', 'K-5', 'K-8', '3-5'];
  
  // Filter resources based on selected filters
  const filteredResources = mockResources.filter(resource => {
    const matchesCategory = activeCategory === 'All' || resource.category === activeCategory;
    const matchesGrade = activeGrade === 'All' || 
                         resource.gradeLevel === activeGrade || 
                         resource.gradeLevel.includes(activeGrade);
    const matchesSearch = searchQuery === '' || 
                         resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesGrade && matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Teaching Resources</h1>
        <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700">
          Upload Resource
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label htmlFor="search-resources" className="block text-sm font-medium text-gray-700 mb-1">
              Search Resources
            </label>
            <div className="relative">
              <input
                id="search-resources"
                type="text"
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-primary focus:border-primary"
                placeholder="Search by title or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          
          <div>
            <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Resource Category
            </label>
            <select
              id="category-filter"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary focus:border-primary"
              value={activeCategory}
              onChange={(e) => setActiveCategory(e.target.value)}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Grade Level
            </label>
            <select
              id="grade-filter"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary focus:border-primary"
              value={activeGrade}
              onChange={(e) => setActiveGrade(e.target.value)}
            >
              {gradeOptions.map((grade) => (
                <option key={grade} value={grade}>
                  {grade === 'All' ? 'All Grades' : 
                   grade === 'K' ? 'Kindergarten' : 
                   grade === 'K-5' ? 'Grades K-5' :
                   grade === 'K-8' ? 'Grades K-8' :
                   grade === '3-5' ? 'Grades 3-5' :
                   `Grade ${grade}`}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {filteredResources.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => (
              <div key={resource.id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="h-40 bg-gray-50 flex items-center justify-center">
                  <img src={resource.thumbnail} alt={resource.title} className="max-h-full" />
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{resource.title}</h3>
                    <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${
                      resource.type === 'PDF' ? 'bg-primary-100 text-primary-800' :
                      resource.type === 'Video' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {resource.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{resource.description}</p>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-4">
                    <span>{resource.category}</span>
                    <span>Grade: {resource.gradeLevel}</span>
                  </div>
                  <div className="flex space-x-2">
                    <a 
                      href={resource.downloadUrl} 
                      className="flex-1 text-center bg-primary hover:bg-primary-700 text-white py-2 px-4 rounded-lg text-sm font-medium"
                    >
                      Download
                    </a>
                    <button className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-50">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg 
              className="mx-auto h-12 w-12 text-gray-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1} 
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No resources found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your filters or search to find teaching resources.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourcesScreen; 